import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
const ADDED_STATE_DURATION = 2000;

/**
 * @typedef {object} Variant
 * @property {number} id
 * @property {string[]} options
 * @property {boolean} available
 * @property {string} price
 * @property {string} compare
 * @property {boolean} onSale
 * @property {string} off
 * @property {string} state
 * @property {string} stock
 * @property {number | null} mediaId
 * @property {string} sku
 */

/**
 * Main product page: switches variants without a reload (price, stock, picture, button and URL), the gallery with
 * thumbnails and hover zoom, the quantity stepper, in-place Add to Cart (Buy Now posts the form so checkout opens),
 * and the share button. Without script the form posts to /cart/add. The info rows are native <details> elements.
 *
 * @extends {Component}
 */
class TphProductComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Variant[]} */
  #variants = [];
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#teardown();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  #teardown() {
    this.#abortController?.abort();
    clearTimeout(this.#timer);
  }

  get #form() {
    const form = this.querySelector('form.tph-prod__form');
    return form instanceof HTMLFormElement ? form : null;
  }

  #setup() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    try {
      const data = this.querySelector('script[data-variants]')?.textContent ?? '[]';
      this.#variants = JSON.parse(data);
    } catch {
      this.#variants = [];
    }

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });

    this.#setupZoom(signal);
  }

  /* ---------- Gallery ---------- */

  /** @param {AbortSignal} signal */
  #setupZoom(signal) {
    const stage = this.querySelector('.tph-prod__stage');
    if (!(stage instanceof HTMLElement) || !this.hasAttribute('data-zoom')) return;

    stage.addEventListener(
      'pointerenter',
      (event) => {
        if (event.pointerType === 'mouse' && hoverQuery.matches) stage.classList.add('is-zooming');
      },
      { signal }
    );
    stage.addEventListener('pointerleave', () => stage.classList.remove('is-zooming'), { signal });
    stage.addEventListener(
      'pointermove',
      (event) => {
        const rect = stage.getBoundingClientRect();
        stage.style.setProperty('--tph-zoom-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
        stage.style.setProperty('--tph-zoom-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
      },
      { signal, passive: true }
    );
  }

  /** @param {string | number} mediaId */
  #showMedia(mediaId) {
    const id = String(mediaId);
    for (const slide of this.querySelectorAll('.tph-prod__slide')) {
      if (slide instanceof HTMLElement) slide.classList.toggle('is-active', slide.dataset.mediaId === id);
    }
    for (const thumb of this.querySelectorAll('.tph-prod__thumb')) {
      if (!(thumb instanceof HTMLElement)) continue;
      const active = thumb.dataset.mediaId === id;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-pressed', String(active));
      if (active) thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }

  /* ---------- Variants ---------- */

  /** @returns {string[]} */
  #selectedOptions() {
    const values = [];
    for (const group of this.querySelectorAll('[data-option]')) {
      const input = group.querySelector('[data-option-input]');
      if (input instanceof HTMLSelectElement) {
        values.push(input.value);
      } else {
        const checked = group.querySelector('input[type="radio"]:checked');
        values.push(checked instanceof HTMLInputElement ? checked.value : '');
      }
    }
    return values;
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.matches('[data-option-input]')) return;

    const selected = this.#selectedOptions();
    const variant = this.#variants.find((item) => item.options.every((value, index) => value === selected[index]));
    this.#updateOptionLabels(selected);
    this.#applyVariant(variant);
  };

  /** @param {string[]} selected */
  #updateOptionLabels(selected) {
    let index = 0;
    for (const group of this.querySelectorAll('[data-option]')) {
      const label = group.querySelector('[data-option-value]');
      if (label) label.textContent = selected[index] ?? '';
      index += 1;
    }
  }

  /** @param {Variant | undefined} variant */
  #applyVariant(variant) {
    const form = this.#form;
    const addButton = this.querySelector('[data-add]');
    const addText = this.querySelector('[data-add-text]');
    const buyNow = this.querySelector('[data-buy-now]');
    this.#clearError();

    if (!variant) {
      // This combination does not exist
      if (addButton instanceof HTMLButtonElement) addButton.disabled = true;
      if (addText) addText.textContent = this.dataset.labelSold ?? '';
      if (buyNow instanceof HTMLElement) buyNow.hidden = true;
      return;
    }

    const idInput = form?.querySelector('[data-variant-id]');
    if (idInput instanceof HTMLInputElement) idInput.value = String(variant.id);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState({}, '', url);

    const price = this.querySelector('[data-price]');
    const compare = this.querySelector('[data-compare]');
    const off = this.querySelector('[data-off]');
    if (price) price.textContent = variant.price;
    if (compare instanceof HTMLElement) {
      compare.textContent = variant.compare;
      compare.hidden = !variant.onSale;
    }
    if (off instanceof HTMLElement) {
      off.textContent = variant.off;
      off.hidden = !variant.onSale;
    }

    const stock = this.querySelector('[data-stock]');
    if (stock instanceof HTMLElement) {
      stock.classList.remove('tph-prod__stock--in', 'tph-prod__stock--low', 'tph-prod__stock--out');
      stock.classList.add(`tph-prod__stock--${variant.state}`);
      const text = stock.querySelector('[data-stock-text]');
      if (text) text.textContent = variant.stock;
    }

    const sku = this.querySelector('[data-sku]');
    if (sku) sku.textContent = variant.sku;

    if (addButton instanceof HTMLButtonElement) addButton.disabled = !variant.available;
    if (addText) addText.textContent = variant.available ? (this.dataset.labelAdd ?? '') : (this.dataset.labelSold ?? '');
    if (buyNow instanceof HTMLElement) buyNow.hidden = !variant.available;

    if (variant.mediaId) this.#showMedia(variant.mediaId);
  }

  /* ---------- Clicks: gallery, quantity, share ---------- */

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const thumb = target.closest('.tph-prod__thumb');
    if (thumb instanceof HTMLElement && thumb.dataset.mediaId) {
      this.#showMedia(thumb.dataset.mediaId);
      return;
    }

    const step = target.closest('[data-qty-step]');
    if (step instanceof HTMLElement) {
      this.#stepQuantity(Number(step.dataset.qtyStep));
      return;
    }

    const share = target.closest('[data-share]');
    if (share instanceof HTMLElement) {
      this.#share(share);
      return;
    }

  };

  /** @param {number} delta */
  #stepQuantity(delta) {
    const input = this.querySelector('[data-qty]');
    if (!(input instanceof HTMLInputElement)) return;
    const next = Math.max(1, (Number(input.value) || 1) + delta);
    input.value = String(next);
  }

  /** @param {HTMLElement} button */
  async #share(button) {
    const url = window.location.href;
    const title = button.dataset.title ?? document.title;

    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      this.#announce(this.dataset.labelCopied ?? '');
    } catch {
      // Sharing was cancelled or is not allowed; nothing else to do
    }
  }

  /* ---------- Cart ---------- */

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('tph-prod__form')) return;

    // Buy Now posts the form as it is, so Shopify adds the item and opens checkout
    const submitter = event.submitter;
    if (submitter instanceof HTMLButtonElement && submitter.name === 'checkout') return;

    event.preventDefault();
    const button = form.querySelector('[data-add]');
    if (!(button instanceof HTMLButtonElement) || button.getAttribute('aria-busy') === 'true') return;
    this.#addToCart(form, button);
  };

  /**
   * @param {HTMLFormElement} form
   * @param {HTMLButtonElement} button
   */
  async #addToCart(form, button) {
    const data = new FormData(form);
    const variantId = String(data.get('id') ?? '');
    const quantity = Math.max(1, Number(data.get('quantity')) || 1);
    if (!variantId) return;

    button.setAttribute('aria-busy', 'true');
    this.#clearError();

    const sectionIds = [];
    for (const item of document.querySelectorAll('cart-items-component')) {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity }],
        promise: deferred.promise,
      })
    );

    const detail = { source: 'tph-product-component', sourceId: this.id, itemCount: quantity };

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity }], sections: sectionIds.join(',') }),
      });
      const result = await response.json();

      if (result.status) {
        const message = result.description || result.message || this.dataset.error || '';
        form.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        const cart = await this.#fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detail, items: cart.items, didError: true },
        });
        this.#showError(message);
        return;
      }

      const cart = await this.#fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { ...detail, items: cart.items, sections: result.sections, didError: false },
      });
      this.#showAdded(button);
      this.#announce(this.dataset.labelAdded ?? '');
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      form.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#showError(this.dataset.error ?? '');
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  /** @returns {Promise<any>} */
  async #fetchCart() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      return /** @type {any} */ (cartItems).fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /** @param {HTMLButtonElement} button */
  #showAdded(button) {
    const text = button.querySelector('[data-add-text]');
    if (!text) return;
    const original = this.dataset.labelAdd ?? text.textContent ?? '';
    text.textContent = Theme.translations.added;
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => {
      text.textContent = original;
    }, ADDED_STATE_DURATION);
  }

  /** @param {string} message */
  #showError(message) {
    const error = this.querySelector('[data-error-text]');
    if (!(error instanceof HTMLElement) || !message) return;
    error.textContent = message;
    error.hidden = false;
  }

  #clearError() {
    const error = this.querySelector('[data-error-text]');
    if (error instanceof HTMLElement) {
      error.hidden = true;
      error.textContent = '';
    }
  }

  /** @param {string} message */
  #announce(message) {
    const announcer = this.querySelector('[data-announcer]');
    if (!(announcer instanceof HTMLElement) || !message) return;
    announcer.textContent = '';
    requestAnimationFrame(() => {
      announcer.textContent = message;
    });
  }
}

if (!customElements.get('tph-product-component')) {
  customElements.define('tph-product-component', TphProductComponent);
}
